import { toast } from "react-toastify";
import { reportUnexpectedError } from "../../../../error-reporter";
import apiClient from "../../../apis/tenant-management/tenant-management-client";
import { BaseApplicationService } from "../../../domain/base/base-application-service";
import { GroupMetaData } from "../../../domain/group/group";
import { UserEntity } from "../../../domain/user/user-entity";

export type MyAccountServiceProps = {
  isLoading: boolean;
  userId: string;
  user?: UserEntity;
  userGroup?: GroupMetaData[];
  setUserId: typeof MyAccountService.prototype.setUserId;
  fetchUser: typeof MyAccountService.prototype.fetchUser;
  issueEmailToken: typeof MyAccountService.prototype.issueEmailToken;
  verifyEmailToken: typeof MyAccountService.prototype.verifyEmailToken;
  resetUserEmail: typeof MyAccountService.prototype.resetUserEmail;
};

export class MyAccountService extends BaseApplicationService<MyAccountServiceProps> {
  // ユーザーが所属しているグループの表示件数上限
  private readonly groupDisplayCount = 100;

  state = {
    isLoading: false,
    userId: "",
    user: undefined as UserEntity | undefined,
    userGroup: [],
    setUserId: this.setUserId.bind(this),
    fetchUser: this.fetchUser.bind(this),
    issueEmailToken: this.issueEmailToken.bind(this),
    verifyEmailToken: this.verifyEmailToken.bind(this),
    resetUserEmail: this.resetUserEmail.bind(this),
  };

  setUserId(userId: string) {
    this.setState({ userId: userId });
  }

  async fetchUser() {
    this.setIsLoading(true);
    try {
      const { data: user } = await apiClient.fetchUser(this.state.userId);
      const { data: groups } = await apiClient.fetchGroupsMetadata(
        1,
        this.groupDisplayCount,
        {
          ids: user.groupIDs,
        },
      );
      this.setState({ user: user, userGroup: groups });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("ユーザー情報の取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async resetUserEmail() {
    this.setIsLoading(true);
    try {
      await apiClient.resetUserEmail(this.state.userId);
      await this.fetchUser();
    } catch (e) {
      throw e;
    } finally {
      this.setIsLoading(false);
    }
  }

  async issueEmailToken(email: string) {
    this.setIsLoading(true);
    try {
      await apiClient.issueUserMailToken(this.state.userId, email);
    } catch (e) {
      throw e;
    } finally {
      this.setIsLoading(false);
    }
  }

  async verifyEmailToken(email: string, token: string) {
    this.setIsLoading(true);
    try {
      await apiClient.verifyUserMailToken(this.state.userId, email, token);
      await this.fetchUser();
    } catch (e) {
      throw e;
    } finally {
      this.setIsLoading(false);
    }
  }

  private setIsLoading(isLoading: boolean) {
    this.setState({ isLoading: isLoading });
  }
}
